import { useCallback, useSyncExternalStore } from 'react'
import type { ColumnDef } from './types'

const PREFS_PREFIX = 'easycrm:table:'
const SAME_TAB_EVENT = 'easycrm:table-prefs-changed'

export interface ColumnPrefs {
  /** Column keys explicitly hidden by the user. */
  hidden: string[]
  /** Column key order. Keys missing from this list append at the end of the default order. */
  order: string[]
}

const EMPTY_PREFS: ColumnPrefs = { hidden: [], order: [] }

// Single source of truth across all useColumnPrefs(sameTableId) instances within a tab.
// useSyncExternalStore + this cache means DataTable and ColumnSettingsPopover stay in sync.
const cache = new Map<string, ColumnPrefs>()

function prefsKey(tableId: string): string {
  return `${PREFS_PREFIX}${tableId}:cols`
}

function readFromStorage(tableId: string): ColumnPrefs {
  try {
    const raw = localStorage.getItem(prefsKey(tableId))
    if (!raw) return EMPTY_PREFS
    const parsed = JSON.parse(raw) as Partial<ColumnPrefs>
    return {
      hidden: Array.isArray(parsed.hidden) ? parsed.hidden : [],
      order: Array.isArray(parsed.order) ? parsed.order : [],
    }
  } catch {
    return EMPTY_PREFS
  }
}

function getSnapshot(tableId: string): ColumnPrefs {
  const cached = cache.get(tableId)
  if (cached) return cached
  const fresh = readFromStorage(tableId)
  cache.set(tableId, fresh)
  return fresh
}

function setPrefs(tableId: string, next: ColumnPrefs): void {
  cache.set(tableId, next)
  try {
    localStorage.setItem(prefsKey(tableId), JSON.stringify(next))
  } catch {
    // localStorage unavailable - state persists in cache for this tab session only
  }
  window.dispatchEvent(new CustomEvent(SAME_TAB_EVENT, { detail: { tableId } }))
}

function subscribe(tableId: string, callback: () => void): () => void {
  const sameTabHandler = (e: Event) => {
    if ((e as CustomEvent).detail?.tableId === tableId) callback()
  }
  const storageHandler = (e: StorageEvent) => {
    if (e.key === prefsKey(tableId)) {
      cache.delete(tableId)
      callback()
    }
  }
  window.addEventListener(SAME_TAB_EVENT, sameTabHandler)
  window.addEventListener('storage', storageHandler)
  return () => {
    window.removeEventListener(SAME_TAB_EVENT, sameTabHandler)
    window.removeEventListener('storage', storageHandler)
  }
}

export function useColumnPrefs(tableId: string) {
  const prefs = useSyncExternalStore(
    (cb) => subscribe(tableId, cb),
    () => getSnapshot(tableId),
    () => EMPTY_PREFS,
  )

  const reset = useCallback(() => setPrefs(tableId, EMPTY_PREFS), [tableId])

  const toggleHidden = useCallback(
    (key: string) => {
      const cur = getSnapshot(tableId)
      const isHidden = cur.hidden.includes(key)
      setPrefs(tableId, {
        ...cur,
        hidden: isHidden ? cur.hidden.filter((k) => k !== key) : [...cur.hidden, key],
      })
    },
    [tableId],
  )

  const setOrder = useCallback(
    (keys: string[]) => {
      const cur = getSnapshot(tableId)
      setPrefs(tableId, { ...cur, order: keys })
    },
    [tableId],
  )

  return { prefs, reset, toggleHidden, setOrder }
}

/** Pure helper - applies user prefs (order + hidden) onto a permission-filtered column list. */
export function applyColumnPrefs<TRow>(
  columns: ColumnDef<TRow>[],
  prefs: ColumnPrefs,
): { ordered: ColumnDef<TRow>[]; visible: ColumnDef<TRow>[] } {
  const byKey = new Map(columns.map((c) => [c.key, c]))
  const ordered: ColumnDef<TRow>[] = []
  for (const key of prefs.order) {
    const col = byKey.get(key)
    if (col) {
      ordered.push(col)
      byKey.delete(key)
    }
  }
  for (const col of byKey.values()) {
    ordered.push(col)
  }
  const visible = ordered.filter((c) => c.alwaysVisible || !prefs.hidden.includes(c.key))
  return { ordered, visible }
}
